import { ChangeDetectionStrategy,Component, inject, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ExternalPhotoSourceStatus, GenericExternalPhotoSourceConfig } from 'app/shared/graph';
import { StoreLocalesService } from 'app/shared/store-locales/store-locales.service';
import { TranslatableLabelFieldComponent } from 'app/shared/store-locales/translatable-label-field.component';
import { blobToBase64 } from 'app/shared/utilities/file-helpers';

import { ExternalPhotosConfigService } from './external-photos-config.service';

@Component({
    templateUrl: './generic-external-photo-source-edit-page.component.html',
    styleUrls: ['./generic-external-photo-source-edit-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageSimpleContentComponent,
        FormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatHint,
        SaveButtonComponent,
        ErrorComponent,
        MatIconButton,
        MatIcon,
        MatSuffix,
        TranslatableLabelFieldComponent,
    ],
})
export class GenericExternalPhotoSourceEditPageComponent implements OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly photosConfigService = inject(ExternalPhotosConfigService);
    private readonly router = inject(Router);
    private readonly snackBar = inject(MatSnackBar);
    private readonly storeLocalesService = inject(StoreLocalesService);

    public mode: 'edit' | 'create';
    public title = '';
    public storeId: string;
    public photoSourceId: string;
    public pageStatus: PageStatus = PageStatus.loading;
    public pageError?: Error;
    public labelTranslations = signal<Record<string, string>>({});
    public supportedLocales = signal<string[]>([]);
    public labelLocalizationKey = signal<string>('');

    public genericExternalSource ?: GenericExternalPhotoSourceConfig;
    public appSecret ?: string;
    private redirectTimerId?: number;

    constructor() {
        this.storeId = this.route.snapshot.params.storeId;
        this.photoSourceId = this.route.snapshot.params.photoSourceId;
        this.mode = this.route.snapshot.data.mode;
        this.labelLocalizationKey.set('hub.sources.generic.' + this.photoSourceId + ".label");
        if (this.mode === 'edit') {
            this.title = 'Edit Generic External Photo Source';
            this.storeLocalesService.getStoreLocalizedLabel(this.storeId, this.labelLocalizationKey()).subscribe((translations) => {
                this.labelTranslations.set(translations);
            });
            this.storeLocalesService.listStoreSupportedLocales(this.storeId).subscribe((locales) => {
                this.supportedLocales.set(locales);
            });
            this.photosConfigService.loadGenericExternalPhotoSource(this.storeId, this.photoSourceId)
                .subscribe({
                    next: source => {
                        if (!source) {
                            this.pageStatus = PageStatus.loadError;
                            this.pageError = new Error('Photo source not found');
                            return;
                        }

                        this.genericExternalSource = source;
                        this.pageStatus = PageStatus.loaded;
                    },
                    error: err => {
                        this.pageStatus = PageStatus.loadError;
                        this.pageError = err;
                    },
                });
        } else {
            this.title = 'Add New Generic External Photo Source';

            this.genericExternalSource = {
                albumUrl: '',
                appId: '',
                authorizationUrl: '',
                icon: '',
                label: '',
                photoUrl: '',
                photoSourceId: '',
                status: ExternalPhotoSourceStatus.Disabled,
                store: { id: this.storeId } as any,
                tokenUrl: '',
            };
            this.pageStatus = PageStatus.loaded;
        }

    }

    save() {
        if (!this.genericExternalSource) {
            return;
        }
        if (this.mode == 'edit') {
            forkJoin([
                this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.labelLocalizationKey(), this.labelTranslations()),
                this.photosConfigService.updateGenericExternalPhotoSource({
                    storeId: this.storeId,
                    photoSourceId: this.photoSourceId,
                    appId: this.genericExternalSource.appId,
                    appSecret: this.appSecret,
                    label: this.genericExternalSource.label,
                    scopes: this.genericExternalSource.scopes,
                    icon: this.genericExternalSource.icon,
                    photoUrl: this.genericExternalSource.photoUrl,
                    albumUrl: this.genericExternalSource.albumUrl,
                    tokenUrl: this.genericExternalSource.tokenUrl,
                    authorizationUrl: this.genericExternalSource.authorizationUrl,
                }),
            ]).subscribe({
                next: ([, result]) => this.completeSave(result),
                error: this.saveError.bind(this),
            });
        } else {
            if (!this.appSecret) {
                return;
            }

            let input = {
                storeId: this.storeId,
                photoSourceId: this.photoSourceId,
                appId: this.genericExternalSource.appId,
                appSecret: this.appSecret,
                label: this.genericExternalSource.label,
                scopes: this.genericExternalSource.scopes,
                icon: this.genericExternalSource.icon,
                photoUrl: this.genericExternalSource.photoUrl,
                albumUrl: this.genericExternalSource.albumUrl,
                tokenUrl: this.genericExternalSource.tokenUrl,
                authorizationUrl: this.genericExternalSource.authorizationUrl,
            };
            forkJoin([
                this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.labelLocalizationKey(), this.labelTranslations()),
                this.photosConfigService.createGenericExternalPhotoSource(input),
            ]).subscribe({
                next: ([, result]) => this.completeSave(result),
                error: this.saveError.bind(this),
            });
        }

        this.pageStatus = PageStatus.saving;
    }

    protected async onThumbnailSelected(files: FileList | null) {
        if (!files) {
            return;
        }
        let file = files[0];
        let fileExtension = file.name.split('.').pop();
        if (!fileExtension) {
            return;
        }
        this.photosConfigService.uploadAssetEditorFile({
            storeId: this.storeId,
            filename: 'photo-sources/external.' + this.photoSourceId + '.thumbnail.' + fileExtension,
            contentBase64: await blobToBase64(file),
        }).subscribe({
            next: (result) => {
                if (this.genericExternalSource) {
                    let url = new URL(result.publicUrl);
                    if (result.contentHash) {
                        url.searchParams.set('h', result.contentHash);
                    }
                    this.genericExternalSource.icon = url.toString();
                }
            },
            error: (e: Error) => {
                this.snackBar.open('Failed to upload thumbnail: ' + e.message, 'Dismiss');
            },
        });
    }

    private saveError(err: Error) {
        this.pageStatus = PageStatus.loadError;
        this.pageError = err;
    }

    private completeSave(source: GenericExternalPhotoSourceConfig) {
        this.pageStatus = PageStatus.loaded;
        this.genericExternalSource = source;
        this.appSecret = undefined;
        this.redirectTimerId = window.setTimeout(() => {
            this.router.navigate(['stores', this.storeId, 'settings', 'external-photo-sources']);
        }, 500);
    }

    ngOnDestroy(): void {
        if (this.redirectTimerId) {
            clearTimeout(this.redirectTimerId);
            this.redirectTimerId = undefined;
        }
    }
}
